import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Pause } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ServiceStatus {
  paused: boolean;
  message: string;
}

interface PausedContextValue {
  status: ServiceStatus;
  showPausedModal: () => void;
  refresh: () => Promise<void>;
}

const DEFAULT_MSG = "New user access is temporarily paused. We will reopen soon. Stay tuned.";

const PausedContext = createContext<PausedContextValue>({
  status: { paused: false, message: DEFAULT_MSG },
  showPausedModal: () => {},
  refresh: async () => {},
});

export const usePausedModal = () => useContext(PausedContext);
export const useServiceStatus = () => useContext(PausedContext).status;

export const OnboardingPausedProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ServiceStatus>({ paused: false, message: DEFAULT_MSG });

  const refresh = useCallback(async () => {
    const { data } = await (supabase as any)
      .from("service_settings")
      .select("onboarding_paused, paused_message")
      .eq("id", 1)
      .maybeSingle();
    if (data) {
      setStatus({
        paused: !!data.onboarding_paused,
        message: data.paused_message || DEFAULT_MSG,
      });
    }
  }, []);

  useEffect(() => {
    refresh();
    // Realtime sync so toggling in CRM updates the live site immediately
    const channel = (supabase as any)
      .channel("service-settings")
      .on("postgres_changes", { event: "*", schema: "public", table: "service_settings" }, () => refresh())
      .subscribe();
    return () => { (supabase as any).removeChannel(channel); };
  }, [refresh]);

  const showPausedModal = useCallback(() => setOpen(true), []);

  return (
    <PausedContext.Provider value={{ status, showPausedModal, refresh }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md border-primary/20 bg-card">
          <DialogHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              <Pause className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-center text-xl font-display">Access Temporarily Paused</DialogTitle>
            <DialogDescription className="text-center pt-2 text-foreground/80 leading-relaxed">
              We are currently not accepting new users as we are operating at full capacity.
              <br /><br />
              We will notify once access reopens. Please stay tuned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button onClick={() => setOpen(false)} className="bg-primary text-primary-foreground hover:bg-primary/90 min-w-[140px]">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PausedContext.Provider>
  );
};

export const PausedBanner = () => {
  const { status } = useContext(PausedContext);
  if (!status.paused) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-primary/90 via-primary to-primary/90 text-primary-foreground shadow-md">
      <div className="container mx-auto px-4 py-2 flex items-center justify-center gap-2 text-center">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <p className="text-xs sm:text-sm font-medium">{status.message}</p>
      </div>
    </div>
  );
};
